import { useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import Card from '../components/card/Card';
import H from '../components/texts/h/H';
import P from '../components/texts/p/P';
import Button from '../components/button/Button';
import Input from '../components/input/Input';
import Icon from '../components/icon/Icon';
import Table from '../components/table/table';
import TableSkeleton from '../components/TableSkeleton/TableSkeleton';
import Tooltip from '../components/tooltip/Tooltip';
import { getGroupedProductLineDetail, getG3Header, getG3Aapl, getG3Avales, annulMonetization, getMonetizationRequestContext } from '../services/GuaranteesService';
import { routes } from '../routes/routePaths';
import { buildPrefilledMonetizationSearchParams } from '../utils/monetizationPrefilledNavigation';
import { useFiscalIdConfig } from '../hooks/useFiscalIdConfig';
import AnnulMonetizationDialog from '../components/dialogs/AnnulMonetizationDialog.tsx';
import GuaranteeAnnulActions from '../components/guarantees/GuaranteeAnnulActions';
import { useToast } from '../components/toast/toast';
import { resolveAnnulInstrumentType } from '../utils/guaranteeAnnulUtils';
import { isLegacyProductLineCodeParam, resolveProductLineCode } from '../utils/productLines';
import type { GuaranteesListState } from '../utils/guaranteesListState';
import PageBreadcrumb from '../components/PageBreadcrumb/PageBreadcrumb';
import { useConfig } from '../context/ConfigContext';
import { resolveLocalCurrencyCode } from '../utils/quotationAssets';
import {
  currencyDisplayLabel,
  isoCodeForGuaranteeCurrency,
} from '../utils/currencyLabel';

// Aggregated detail of a grouped (cuit, productLineCode, currencyId) tuple.
// Acts as the intermediate screen requested in Task 50115: from "Resumen
// agrupado por producto" the eye icon brings the user here so they can see the
// totals of the whole group plus every instrument across all its APLs before
// opening the monetization form. The "+ Nueva monetización" button forwards
// the grouped context so the backend can FIFO-consume across the whole group.

interface GroupedLineSummary {
  application_product_line_id: number;
  application_id: number;
  approved_amount: number;
  consumed_amount: number;
  available_amount: number;
}

interface GroupedGuarantee {
  id: number;
  id_global: string;
  application_product_line_id?: number;
  guarantee_type?: string;
  instrument_type?: string;
  created_at: string | null;
  issue_date: string | null;
  monetization_date: string | null;
  due_date: string | null;
  settlement_date?: string | null;
  creditor_name?: string | null;
  amortization_type_name?: string | null;
  monetization_amount?: number | null;
  net_monetization_amount?: number | null;
  installment_default?: number;
  payment_frequency_type_name?: string | null;
  term_in_days?: number | null;
  installment_current?: number;
  installment_count?: number;
  status_type_name?: string | null;
  monetization_status_type_id?: number | null;
  company_name?: string;
}

interface GroupedDetailData {
  cuit: string;
  company_name: string;
  product_line_id: number;
  product_line_code: string;
  product_line_label: string;
  currency_id: number;
  currency_code: string;
  currency_label: string;
  total_approved_amount: number;
  total_consumed_amount: number;
  total_available_amount: number;
  application_product_line_ids: number[];
  lines: GroupedLineSummary[];
  guarantees: GroupedGuarantee[];
}

interface G3HeaderData {
  cuit: string;
  companyName?: string;
  productId: number;
  productLabel: string;
  currencyId: number;
  currencyLabel: string;
  capProduct: number;
  usedProduct: number;
  availableProduct: number;
  capCuitCurrency: number;
  availableCuitCurrency: number;
  availableOperable: number;
}

const GroupedProductLineDetail = () => {
  const fiscalId = useFiscalIdConfig();
  const localCurrencyCode = resolveLocalCurrencyCode(useConfig());
  const { cuit, productLineCode, currencyId } = useParams<{
    cuit: string;
    productLineCode: string;
    currencyId: string;
  }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [data, setData] = useState<GroupedDetailData | null>(null);
  const [g3Header, setG3Header] = useState<G3HeaderData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [annulDialogOpen, setAnnulDialogOpen] = useState(false);
  const [selectedGuarantee, setSelectedGuarantee] = useState<GroupedGuarantee | null>(null);
  const [annulling, setAnnulling] = useState(false);
  const [loadingMonetizationContext, setLoadingMonetizationContext] = useState(false);

  useEffect(() => {
    if (cuit && productLineCode && currencyId) {
      fetchDetail();
    }
  }, [cuit, productLineCode, currencyId, location.key]);

  const fetchDetail = async () => {
    if (!cuit || !productLineCode || !currencyId) return;
    setLoading(true);
    setError(null);
    try {
      // Fetch G3 header, AAPL list, and avales list from double-cap endpoints
      const [headerRes, aaplRes, avalesRes] = await Promise.all([
        getG3Header(cuit, parseInt(productLineCode), parseInt(currencyId)),
        getG3Aapl(cuit, parseInt(productLineCode), parseInt(currencyId)),
        getG3Avales(cuit, parseInt(productLineCode), parseInt(currencyId))
      ]);
      
      if (headerRes.success && 'data' in headerRes) {
        setG3Header(headerRes.data as G3HeaderData);
        
        // Extract AAPL IDs from the response
        const aaplIds: number[] = (aaplRes.success && 'data' in aaplRes && Array.isArray(aaplRes.data))
          ? aaplRes.data.map((a: any) => a.aaplId)
          : [];
        
        // Extract guarantees from the avales endpoint
        const guaranteesFromApi: GroupedGuarantee[] = (avalesRes.success && 'data' in avalesRes && Array.isArray(avalesRes.data))
          ? avalesRes.data.map((a: any) => ({
              id: a.id,
              id_global: a.idGlobal || String(a.id),
              application_product_line_id: a.applicationProductLineId,
              guarantee_type: a.guaranteeType,
              created_at: a.createdAt,
              issue_date: a.issueDate,
              monetization_date: a.monetizationDate,
              due_date: a.dueDate,
              settlement_date: a.settlementDate,
              creditor_name: a.creditorName,
              amortization_type_name: a.amortizationTypeName,
              monetization_amount: a.monetizationAmount,
              net_monetization_amount: a.netMonetizationAmount,
              installment_default: a.installmentDefault,
              payment_frequency_type_name: a.paymentFrequencyTypeName,
              term_in_days: a.termInDays,
              installment_current: a.installmentCurrent,
              installment_count: a.installmentCount,
              status_type_name: a.statusTypeName,
              monetization_status_type_id: a.monetizationStatusTypeId ?? a.monetization_status_type_id,
              company_name: a.companyName
            }))
          : [];

        const buildFromG3 = (): GroupedDetailData => {
          const h = headerRes.data as G3HeaderData;
          return {
            cuit: h.cuit,
            company_name: h.companyName ?? '',
            product_line_id: h.productId,
            product_line_code: resolveProductLineCode(productLineCode, h.productId),
            product_line_label: h.productLabel,
            currency_id: h.currencyId,
            currency_code: h.currencyLabel,
            currency_label: h.currencyLabel,
            total_approved_amount: h.capProduct,
            total_consumed_amount: h.usedProduct,
            total_available_amount: h.availableProduct,
            application_product_line_ids: aaplIds,
            lines: [],
            guarantees: guaranteesFromApi
          };
        };

        // G2 navigates with productId (e.g. "2"). Legacy grouped BFF matches by
        // catalog code ("002") — skip that call for numeric ids to avoid a 404.
        if (!isLegacyProductLineCodeParam(productLineCode)) {
          setData(buildFromG3());
          return;
        }

        try {
          const detailRes = await getGroupedProductLineDetail(cuit, productLineCode, currencyId);
          if (detailRes.success && 'data' in detailRes) {
            const responseData = detailRes.data as GroupedDetailData;
            if (guaranteesFromApi.length > 0) {
              responseData.guarantees = guaranteesFromApi;
            } else if (!Array.isArray(responseData.guarantees)) {
              responseData.guarantees = [];
            } else {
              responseData.guarantees = responseData.guarantees.filter((g: any) => g != null);
            }
            if (!responseData.application_product_line_ids?.length && aaplIds.length) {
              responseData.application_product_line_ids = aaplIds;
            }
            setData(responseData);
          } else {
            setData(buildFromG3());
          }
        } catch {
          setData(buildFromG3());
        }
      } else {
        setError('Error al cargar el detalle agrupado');
        navigate(`${routes.guaranteeDetail.replace(':cuit', cuit || '')}`);
      }
    } catch (err) {
      setError('Error al cargar el detalle agrupado');
      navigate(`${routes.guaranteeDetail.replace(':cuit', cuit || '')}`);
    } finally {
      setLoading(false);
    }
  };

  const handleAnnulClick = (guarantee: GroupedGuarantee) => {
    setSelectedGuarantee(guarantee);
    setAnnulDialogOpen(true);
  };

  const handleAnnulConfirm = async () => {
    if (!selectedGuarantee || !data) return;

    setAnnulling(true);
    try {
      const type = resolveAnnulInstrumentType(
        selectedGuarantee.guarantee_type || selectedGuarantee.instrument_type
      );
      const response = await annulMonetization(type, selectedGuarantee.id);
      if (response.success) {
        setAnnulDialogOpen(false);
        setSelectedGuarantee(null);
        useToast.success({
          title: 'Aval anulado correctamente',
          description: 'El monto fue liberado y ya está disponible nuevamente.',
          id: 'annul-aval-success',
          closeButton: true,
          autoClose: 5000,
          toastId: 'annul-aval-success',
        });
        fetchDetail();
      }
    } catch (err: any) {
      const message = err?.response?.data?.message || err?.message || 'No se pudo anular el aval';
      useToast.error({
        title: 'Error al anular el aval',
        description: message,
        id: 'annul-aval-error',
        closeButton: true,
        autoClose: 7000,
        toastId: 'annul-aval-error',
      });
      console.error('Error annulling monetization:', err);
    } finally {
      setAnnulling(false);
    }
  };

  const handleGoBack = () => {
    navigate(`${routes.guaranteeDetail.replace(':cuit', cuit || '')}`, {
      state: location.state,
    });
  };

  const handleNewMonetization = async () => {
    if (!data || !cuit) return;
    if ((g3Header?.availableOperable ?? 0) <= 0) return;

    setLoadingMonetizationContext(true);
    try {
      const context = await getMonetizationRequestContext(
        cuit,
        data.product_line_id,
        data.currency_id,
      );
      const params = buildPrefilledMonetizationSearchParams(context, { from: 'gda' });
      navigate(`${routes.newRequestLoad}?${params.toString()}`);
    } catch (err) {
      console.error('Error getting monetization context from GDA:', err);
      useToast.error({
        title: 'Error',
        description: 'No se pudo cargar el formulario de monetización. Intentá nuevamente.',
        id: 'gda-monetization-context-error',
        autoClose: 5000,
        closeButton: true,
      });
    } finally {
      setLoadingMonetizationContext(false);
    }
  };

  // Reuse the monetization detail navigation logic that ProductLineDetail uses
  // so the eye icon on each instrument keeps opening the right per-type page.
  const handleViewClicked = (row: GroupedGuarantee) => {
    if (!data) return;
    const code = data.product_line_code;
    const lineId = String(row.application_product_line_id || data.application_product_line_ids?.[0] || '');
    const resolveRouteByInstrumentType = (type: string | undefined): string => {
      const normalizedType = type?.trim().toLowerCase().replace(/-/g, '_');

      if (normalizedType === 'loan') return routes.loanDetail;
      if (normalizedType === 'check') return routes.checkDetail;
      if (normalizedType === 'promissory_note') return routes.promissoryNoteDetail;
      if (normalizedType === 'commercial_endorsement') return routes.commercialEndorsementDetail;

      if (code === '003') return routes.loanDetail;
      if (code === '001' || code === '002') return routes.checkDetail;
      if (code === '006' || code === '007') return routes.promissoryNoteDetail;
      if (code === '004') return routes.commercialEndorsementDetail;

      return routes.loanDetail;
    };

    const target = resolveRouteByInstrumentType(row.instrument_type || row.guarantee_type)
      .replace(':cuit', cuit || '')
      .replace(':lineId', lineId)
      .replace(':id', row.id_global.toString());

    navigate(target, { state: { from: location.pathname, guaranteeCode: code } });
  };

  const formatCurrency = (amount: number) => `${amount.toLocaleString('es-AR')}`;

  const formatDate = (dateString: string | null) => {
    if (!dateString) return '-';
    const [year, month, day] = dateString.split('T')[0].split('-');
    const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
    return date.toLocaleDateString('es-AR');
  };

  const getCurrencyCode = () =>
    isoCodeForGuaranteeCurrency(
      {
        currencyId: data?.currency_id,
        currencyCode: data?.currency_code,
        currencyLabel: data?.currency_label,
      },
      localCurrencyCode
    );

  const getCurrencyDisplay = () =>
    currencyDisplayLabel(getCurrencyCode());

  const getTableTitle = () => {
    if (!data) return '';
    switch (data.product_line_code) {
      case '003':
        return 'Creditos asociados';
      case '001':
      case '002':
        return 'Cheques asociados';
      case '006':
      case '007':
        return 'Pagare asociados';
      case '004':
        return 'Avales comerciales asociados';
      default:
        return 'Garantías asociadas';
    }
  };

  // Per-product columns mirror ProductLineDetail so the user gets a familiar
  // table when they drill into a grouped tuple. The only delta is the extra
  // "Nº línea" column that exposes which APL each instrument belongs to —
  // useful in grouped mode where instruments come from multiple lines.
  const getColumns = () => {
    if (!data) return [];
    const code = data.product_line_code;

    const lineColumn = {
      name: <P variant='secondary-2' type="3" weight="bold" withMargin={false}>Nº línea</P>,
      selector: (row: GroupedGuarantee) => (
        <P variant='secondary-2' type="3" weight="regular" withMargin={false}>
          {row.application_product_line_id ?? '-'}
        </P>
      ),
      minWidth: '90px',
      grow: 0.8,
    };

    const actionsColumn = {
      name: '',
      selector: (row: GroupedGuarantee) => row.id,
      cell: (row: GroupedGuarantee) => (
          <Card display='flex' gap='8px' alignItems='center'>
            <Icon
              name='view_outline'
              variant='info'
              modifier='regular'
              width={24}
              height={24}
              cursor='pointer'
              onClick={() => handleViewClicked(row)}
            />
            <GuaranteeAnnulActions
              monetizationStatusTypeId={row.monetization_status_type_id}
              onAnnulClick={() => handleAnnulClick(row)}
            />
          </Card>
        ),
      minWidth: '80px',
      grow: 0.6,
    };

    if (code === '003') {
      return [
        lineColumn,
        {
          name: <P variant='secondary-2' type="3" weight="bold" withMargin={false}>Nº Aval</P>,
          selector: (row: GroupedGuarantee) => (
            <P variant='secondary-2' type="3" weight="regular" withMargin={false}>{row.id_global}</P>
          ),
          minWidth: '80px', grow: 1,
        },
        {
          name: <P variant='secondary-2' type="3" weight="bold" withMargin={false}>F. creación</P>,
          selector: (row: GroupedGuarantee) => (
            <P variant='secondary-2' type="3" weight="regular" withMargin={false}>{formatDate(row.created_at)}</P>
          ),
          minWidth: '110px', grow: 1.2,
        },
        {
          name: <P variant='secondary-2' type="3" weight="bold" withMargin={false}>F. de emisión</P>,
          selector: (row: GroupedGuarantee) => (
            <P variant='secondary-2' type="3" weight="regular" withMargin={false}>{formatDate(row.issue_date)}</P>
          ),
          minWidth: '110px', grow: 1.2,
        },
        {
          name: <P variant='secondary-2' type="3" weight="bold" withMargin={false}>F. vencimiento</P>,
          selector: (row: GroupedGuarantee) => (
            <P variant='secondary-2' type="3" weight="regular" withMargin={false}>{formatDate(row.due_date)}</P>
          ),
          minWidth: '130px', grow: 1.3,
        },
        {
          name: <P variant='secondary-2' type="3" weight="bold" withMargin={false}>Acreedor</P>,
          selector: (row: GroupedGuarantee) => row.creditor_name || '-',
          cell: (row: GroupedGuarantee) => (
            <Tooltip position='top' msg={row.creditor_name || '-'}>
              <P variant='secondary-2' type="3" weight="regular" withMargin={false}>{row.creditor_name || '-'}</P>
            </Tooltip>
          ),
          minWidth: '100px', grow: 0.8,
        },
        {
          name: <P variant='secondary-2' type="3" weight="bold" withMargin={false}>Bruto</P>,
          selector: (row: GroupedGuarantee) => (
            <P variant='secondary-2' type="3" weight="regular" withMargin={false}>
              {row.monetization_amount ? formatCurrency(row.monetization_amount) : '-'}
            </P>
          ),
          minWidth: '120px', grow: 1.4,
        },
        {
          name: <P variant='secondary-2' type="3" weight="bold" withMargin={false}>Estado</P>,
          selector: (row: GroupedGuarantee) => (
            <P variant='secondary-2' type="3" weight="regular" withMargin={false}>
              {row.status_type_name || '-'}
            </P>
          ),
          minWidth: '100px', grow: 1.2,
        },
        actionsColumn,
      ];
    }

    // Cheques, pagarés y aval comercial comparten layout muy similar
    return [
      lineColumn,
      {
        name: <P variant='secondary-2' type="3" weight="bold" withMargin={false}>Nº Aval</P>,
        selector: (row: GroupedGuarantee) => (
          <P variant='secondary-2' type="3" weight="regular" withMargin={false}>{row.id_global}</P>
        ),
        minWidth: '80px', grow: 1,
      },
      {
        name: <P variant='secondary-2' type="3" weight="bold" withMargin={false}>Socio partícipe / Tercero</P>,
        selector: (row: GroupedGuarantee) => (
          <P variant='secondary-2' type="3" weight="regular" withMargin={false}>
            {row.company_name || data.company_name}
          </P>
        ),
        minWidth: '180px', grow: 2,
      },
      {
        name: <P variant='secondary-2' type="3" weight="bold" withMargin={false}>F. de emisión</P>,
        selector: (row: GroupedGuarantee) => (
          <P variant='secondary-2' type="3" weight="regular" withMargin={false}>{formatDate(row.issue_date)}</P>
        ),
        minWidth: '130px', grow: 1.3,
      },
      {
        name: <P variant='secondary-2' type="3" weight="bold" withMargin={false}>Fecha de vencimiento</P>,
        selector: (row: GroupedGuarantee) => (
          <P variant='secondary-2' type="3" weight="regular" withMargin={false}>{formatDate(row.due_date)}</P>
        ),
        minWidth: '160px', grow: 1.6,
      },
      {
        name: <P variant='secondary-2' type="3" weight="bold" withMargin={false}>Estado</P>,
        selector: (row: GroupedGuarantee) => (
          <P variant='secondary-2' type="3" weight="regular" withMargin={false}>{row.status_type_name || '-'}</P>
        ),
        minWidth: '100px', grow: 1.2,
      },
      {
        name: <P variant='secondary-2' type="3" weight="bold" withMargin={false}>Monto bruto</P>,
        selector: (row: GroupedGuarantee) => (
          <P variant='secondary-2' type="3" weight="regular" withMargin={false}>
            {row.monetization_amount ? formatCurrency(row.monetization_amount) : '-'}
          </P>
        ),
        minWidth: '120px', grow: 1.4,
      },
      actionsColumn,
    ];
  };

  const renderSummaryCard = (title: string, amount: number, showCurrency: boolean = true) => (
    <Card variant='neutral' modifier='regular' spacing='4'>
      <H variant='secondary-2' withMargin={false} type='4' weight='bold' modifier='strong'>
        {title}
      </H>
      <Card display='flex' alignItems='center' gap='8px'>
        <H withMargin={false} variant='secondary-2' type='2' weight='bold' modifier='strong'>
          {showCurrency ? `${getCurrencyCode()} ${formatCurrency(amount)}` : formatCurrency(amount)}
        </H>
      </Card>
    </Card>
  );

  const listState = location.state as GuaranteesListState | null;
  const companyCrumb =
    data?.company_name?.trim() ||
    g3Header?.companyName?.trim() ||
    listState?.companyName?.trim() ||
    cuit ||
    '';
  const breadcrumb = (
    <PageBreadcrumb
      items={[
        { label: 'Inicio', to: routes.dashboard },
        { label: 'Gestión de avales', to: routes.guarantees, state: listState ?? undefined },
        ...(companyCrumb ? [{ label: companyCrumb }] : []),
      ]}
    />
  );

  if (loading) {
    return (
      <>
        <div style={{ width: '85vw' }}>{breadcrumb}</div>
      <Card variant='neutral' modifier='inverted' spacing={'4'} height='-webkit-fill-available' width='85vw'>
        <Card margin={{ top: '4' }}>
          <TableSkeleton rows={8} columns={4} />
        </Card>
      </Card>
      </>
    );
  }

  if (error || !data) {
    return (
      <>
        <div style={{ width: '85vw' }}>{breadcrumb}</div>
      <Card variant='neutral' modifier='inverted' spacing={'4'} height='-webkit-fill-available' width='85vw'>
        <Card display='flex' justifyContent='center' alignItems='center' height='300px'>
          <P type='1' weight='regular' modifier='weak'>{error || 'Detalle agrupado no encontrado'}</P>
        </Card>
      </Card>
      </>
    );
  }

  return (
    <>
      <div style={{ width: '85vw' }}>{breadcrumb}</div>
    <Card variant='neutral' modifier='inverted' spacing={'4'} height='-webkit-fill-available' width='85vw'>
      <Card display='flex' alignItems='center' gap='16px' margin={{ bottom: '4' }}>
        <Button
          icon='arrow_left_outline'
          variant='tertiary'
          size='sm'
          iconAlign='center'
          onClick={handleGoBack}
          aria-label='Volver a gestión de avales'
        />
        <Card display='flex' alignItems='baseline' gap='8px'>
          <H withMargin={false} variant='secondary-2' type='2' weight='bold' modifier='strong'>
            {data.product_line_label}
          </H>
          <H variant='secondary-2' type='3' weight='bold' modifier='strong'>
            {getCurrencyDisplay()}
          </H>
        </Card>
      </Card>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px', marginBottom: '24px' }}>
        <div>
          <Input label='Razón social' value={data.company_name} readOnly size='md' modifier='weak' />
        </div>
        <div>
          <Input label={fiscalId.label} value={fiscalId.format(data.cuit)} readOnly size='md' modifier='weak' />
        </div>
        <div>
          <Input label='Tipo de operación' value={data.product_line_label} readOnly size='md' modifier='weak' />
        </div>
        <div>
          <Input label='Divisa' value={getCurrencyDisplay()} readOnly size='md' modifier='weak' />
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px', marginBottom: '24px' }}>
        {renderSummaryCard('Linea producto', g3Header?.capProduct ?? data.total_approved_amount)}
        {renderSummaryCard('Utilizado', g3Header?.usedProduct ?? data.total_consumed_amount)}
        {renderSummaryCard('Disponible Operable', g3Header?.availableOperable ?? 0, false)}
      </div>

      {(g3Header?.availableOperable ?? 0) <= 0 && (
        <Card variant='warning' modifier='weak' spacing='3' margin={{ bottom: '4' }}>
          <P variant='secondary-2' type="3" weight="regular" withMargin={false}>
            Este producto no tiene cupo operable: las líneas que lo incluyen ya agotaron su disponible por divisa.
            No se pueden cargar avales hasta que se libere cupo (por anulación de otros avales o nuevas líneas).
          </P>
        </Card>
      )}

      <Card variant='info' modifier='weak' spacing='3' margin={{ bottom: '4' }}>
        <Card display='flex' gap='24px' alignItems='center' justifyContent='center'>
          <P variant='secondary-2' type="3" weight="regular" withMargin={false}>
            Utilizado Linea Cliente{' '}
            <strong style={{ color: 'var(--primary-color)' }}>{getCurrencyCode()} {formatCurrency(g3Header?.capCuitCurrency ?? 0)}</strong>
          </P>
          <P variant='secondary-2' type="3" weight="regular" withMargin={false}>
            Disponible Linea Cliente{' '}
            <strong style={{ color: 'var(--primary-color)' }}>{getCurrencyCode()} {formatCurrency(g3Header?.availableCuitCurrency ?? 0)}</strong>
          </P>
        </Card>
      </Card>

      <Card display='flex' justifyContent='space-between' alignItems='center' margin={{ bottom: '3' }}>
        <H withMargin={false} type='2' weight='bold' modifier='strong'>
          {getTableTitle()}
        </H>
        <Button
          variant='secondary'
          icon='plus_icon'
          iconAlign='left'
          onClick={handleNewMonetization}
          disabled={(g3Header?.availableOperable ?? 0) <= 0 || loadingMonetizationContext}
        >
          Nueva monetización
        </Button>
      </Card>

      <Card margin={{ top: '3' }}>
        {data.guarantees && data.guarantees.length > 0 ? (
          <Table
            columns={getColumns()}
            data={data.guarantees}
            tableData={{
              page: 0,
              size: data.guarantees.length,
              totalElements: data.guarantees.length,
              totalPages: 1,
            }}
            handleChangePage={() => {}}
          />
        ) : (
          <Card display='grid' justifyContent='center' margin={{ top: '11', bottom: '11' }} width='100%'>
            <Card display='flex' justifyContent='center' margin={{ bottom: '2' }}>
              <P variant='primary' align='center' withMargin={false} weight='bold'>
                No se encontraron registros
              </P>
            </Card>
          </Card>
        )}
      </Card>

      <AnnulMonetizationDialog
        isOpen={annulDialogOpen}
        onClose={() => { setAnnulDialogOpen(false); setSelectedGuarantee(null); }}
        onConfirm={handleAnnulConfirm}
        guarantee={selectedGuarantee ? {
          id_global: selectedGuarantee.id_global,
          monetization_amount: selectedGuarantee.monetization_amount,
          application_product_line_id: selectedGuarantee.application_product_line_id,
          product_label: data?.product_line_label,
          currency_code: data?.currency_code,
        } : null}
        isLoading={annulling}
      />
    </Card>
    </>
  );
};

export default GroupedProductLineDetail;
